import axios from 'axios';
import { Client, Device } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://rytwhv6cal.execute-api.ap-southeast-6.amazonaws.com/dev';

const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const adminToken = localStorage.getItem('token');
  const clientToken = localStorage.getItem('clientToken');
  const token = adminToken || clientToken;
  
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export { api };

export const authAPI = {
  login: (username: string, password: string, otp?: string) =>
    api.post('/auth/login', { username, password, ...(otp && { otp }) }),
  register: (username: string, email: string, password: string) =>
    api.post('/auth/register', { username, email, password }),
  forgotPassword: (email: string) =>
    api.post('/auth/forgot-password', { email }),
};

export const clientAPI = {
  getAll: () => api.get('/clients'),
  getById: (id: string | number) => api.post('/clients/get', { clientId: id }),
  create: (client: Omit<Client, 'id'>) => api.post('/clients', client),
  update: (id: string | number, client: Partial<Client>) => api.put(`/clients/${id}`, client),
  delete: (id: string | number) => api.delete(`/clients/${id}`),
  addDevice: (clientId: string | number, device: Omit<Device, 'id' | 'client_id'>) =>
    api.post('/devices', { clientId, ...device }),
  updateDevice: (clientId: string | number, deviceId: string | number, device: Omit<Device, 'id' | 'client_id' | 'created_at'>) =>
    api.put('/devices', { deviceId, clientId, ...device }),
  deleteDevice: (deviceId: string | number) => api.delete(`/devices/${deviceId}`),
  updateNFCLabels: (clientId: string | number, labelCount: number) =>
    api.put('/nfc-labels', { clientId, label_count: labelCount }),
  getComments: (clientId: string | number) => api.post('/comments/get', { clientId }),
  addComment: (clientId: string | number, comment: { content: string; created_by: string }) =>
    api.post('/comments', { clientId, ...comment }),
  removeDuplicates: () => api.post('/devices/remove-duplicates'),
};

export const deviceAPI = {
  getByClientId: (clientId: string | number) => api.get(`/devices/client/${clientId}`),
  getMyDevices: () => api.get('/devices/my-devices'),
};

export const historyAPI = {
  getAll: () => api.get('/history'),
  create: (activity: { action: string; details: string; user_name: string; client_id?: string }) => 
    api.post('/history', activity),
};

export const userAPI = {
  getAll: () => api.get('/users'),
  create: (user: { username: string; email: string; phone?: string }) => api.post('/users', user),
  update: (id: string, user: { username: string; email: string; password?: string; phone?: string }) => api.put(`/users/${id}`, user),
  delete: (id: string) => api.delete(`/users/${id}`),
};

export const reportsAPI = {
  getClientSummary: () => api.get('/reports/client-summary'),
  getDeviceStatus: () => api.get('/reports/device-status'),
  getKPIs: () => api.get('/reports/kpis'),
};

export const ticketsAPI = {
  getAll: (params?: { status?: string; priority?: string; client_id?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.append('status', params.status);
    if (params?.priority) searchParams.append('priority', params.priority);
    if (params?.client_id) searchParams.append('client_id', params.client_id);
    return api.get(`/tickets?${searchParams}`);
  },
  getById: (id: number) => api.get(`/tickets/${id}`),
  create: (ticket: any) => api.post('/tickets', ticket),
  update: (id: number, updates: any) => api.put(`/tickets/${id}`, updates),
  delete: (id: number) => api.delete(`/tickets/${id}`),
  addComment: (id: number, comment: { comment: string }) => api.post(`/tickets/${id}/comments`, comment),
};

export const clientActivityAPI = {
  getAll: () => api.get('/client-activity/all'),
  getByClient: (clientId: number) => api.get(`/client-activity/client/${clientId}`),
  log: (activity: { client_id: number; activity_type: string; description: string; ip_address?: string; user_agent?: string }) => 
    api.post('/client-activity/log', activity),
  invite: (invitation: { client_id: number; invited_by: string }) => 
    api.post('/client-activity/invite', invitation),
};
